import styles from './SelectFeatureAttributeTable.module.scss';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import Table from 'client/components/aria/Table';
import { Cell, Column, Row, TableBody, TableHeader } from 'react-stately';
import PropTypes from 'prop-types';

/**
 * Creates all tables for a section, containing an attribute group title, and displaying the relevant attributes.
 *
 * @param layerMapConfigProps The list of all attributes contained within the layer/for the asset type. Used for generic row building function
 * @returns {JSX.Element} A table containing the relevant information, or a notification if no attributes match or have a default value.
 */
const SelectFeatureAttributeTable = ({ sectionProperties, ...props }) => {
    const currentLocale = useSelector(state => state?.locale?.current);
    return sectionProperties.map(group => {
        if (!group.attributes?.length) {
            return null;
        }

        const dataTestId = props['data-testid'] ? `${props['data-testid']}.${group.displayName[currentLocale]}` : null;
        const tableText = [group.displayName[currentLocale]];
        group.attributes.forEach(attribute => {
            if (!attribute.row) {
                tableText.push(`${attribute.displayName[currentLocale]}: ${attribute.value}`);
            }
        });

        return <div className={['govuk-grid-row', styles.fixHorizontalScrollBug].join(' ')} key={group.displayName[currentLocale]}>
            <div className={['govuk-grid-column-full', styles.noPadding].join(' ')}>
                <Table
                    aria-label={tableText.join(' ')}
                    data-testid={dataTestId}
                    caption={group.displayName[currentLocale]}
                    captionSize='s'
                    hideHeader
                >
                    <TableHeader>
                        <Column className={'govuk-!-width-one-half'}>
                            <FormattedMessage id="selectFeatureAttributeTable.column.property"/>
                        </Column>
                        <Column className={'govuk-!-width-one-half'}>
                            <FormattedMessage id="selectFeatureAttributeTable.column.value"/>
                        </Column>
                    </TableHeader>
                    <TableBody>
                        {group.attributes.map(attribute => {
                            return attribute.row
                                ? attribute.row
                                : <Row
                                    key={[
                                        group.displayName[currentLocale],
                                        attribute.displayName[currentLocale]
                                    ].join('-')}>
                                    <Cell>{attribute.displayName[currentLocale]}</Cell>
                                    <Cell>{attribute.value}</Cell>
                                </Row>;
                        }) }
                    </TableBody>
                </Table>
            </div>
        </div>;
    });
};

SelectFeatureAttributeTable.propTypes = {
    sectionProperties: PropTypes.array,
    'data-testid': PropTypes.string
};

export default SelectFeatureAttributeTable;
